/**
 * Video Script Global Tool
 *
 * Two-stage video script generation:
 * - Stage 1: Analyze content and ask clarifying questions (Perplexity sonar-reasoning-pro)
 * - Stage 2: Generate the script with streaming output (Claude)
 *
 * Can use content from research reports, formatted reports, or direct input.
 */

import { buildModelDisplay } from '../modelDisplay';
import {
  runVideoScriptAnalysis,
  runVideoScriptGeneration,
  formatVideoScriptError,
} from '../../videoScriptService';
import { getLatestOutput, getLatestOutputBySource } from '../workflowOutputStore';
import { analyzeDocumentsWithClaude, hasAnalyzableFiles } from '../../claudeDocumentAnalyzer';
import { finalizeAttachmentReadComplete } from '../messaging';
import { CLAUDE_DOCUMENT_ANALYSIS_DISPLAY } from '../../../config/agenticWorkflowConfig';
import {
  PERPLEXITY_VIDEO_SCRIPT_STAGE1_DISPLAY,
  CLAUDE_VIDEO_SCRIPT_GENERATION_DISPLAY,
} from '../../agenticWorkflows/constants';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import {
  parseVideoScriptTrigger,
  parseVideoScriptStage1Response,
  parseVideoScriptStage2Response,
  formatVideoScriptClarifyingQuestionsHtml,
  formatVideoScriptOutputHtml,
  generateSpecialMessageTemplate,
  isCancelCommand,
} from '../../agenticWorkflows/helpers';

// Import button IDs from the tools that should handle post-video-script actions
import { BUTTON_IDS as TRANSLATE_BUTTON_IDS } from './translate';

import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
  TokenUsageMetadata,
} from '../types';
import type {
  VideoScriptSourceContent,
  VideoScriptStage1Result,
  VideoScriptClarification,
  VideoScriptSummary,
  TranslatableContent,
  ResearchReport,
  DeepResearchSession,
} from '../../agenticWorkflows/types';

import type React from 'react';

import { evaluateTimeRelevance, detectTopicChangeSignals } from '../contextRelevance';
import {
  ENABLE_TIME_BASED_CONTEXT,
  CONTEXT_RELEVANCE_THRESHOLDS,
} from '../../../config/agenticWorkflowConfig';
import { hasNegationAround } from '../triggerHelpers';

// ============================================================================
// Model Display Name Helper
// ============================================================================

// buildModelDisplay: consolidated into ../modelDisplay (2026-06-11) — single
// recording-time codename builder shared by orchestrator + all tools.

// ============================================================================
// Tool State
// ============================================================================

/**
 * State for the video script tool.
 */
export interface VideoScriptState {
  /** Current stage of the workflow */
  stage: 'idle' | 'stage1_analyzing' | 'stage1_clarify' | 'stage2_generating' | 'complete';
  /** Source content for script generation */
  sourceContent: VideoScriptSourceContent | null;
  /** Stage 1 analysis result */
  stage1Analysis: VideoScriptStage1Result | null;
  /** User clarifications provided */
  userClarifications: VideoScriptClarification[];
  /** Final script output */
  scriptOutput?: {
    title: string;
    script: string;
    wordCount: number;
    characterCount: number;
    estimatedLength: string;
  };
  /** Token usage from both stages */
  tokenUsages?: TokenUsageMetadata[];
}

// ============================================================================
// Tool Theme
// ============================================================================

const VIDEO_SCRIPT_THEME: ToolTheme = {
  accent: '#047857',      // Emerald 700 (600 read 3.8:1 as words and behind white — §7a.9, 2026-09-26)
  accentBg: '#d1fae5',    // Emerald 100
  border: '#34d399',      // Emerald 400
  subtitle: '#475569',    // Slate 600
  muted: '#64748b',       // Slate 500
};

// ============================================================================
// Button IDs
// ============================================================================

export const BUTTON_IDS = {
  videoScript: 'global-tool-video-script-btn',
  proceed: 'global-tool-video-script-proceed-btn',
  // Note: translate button ID removed - use imported TRANSLATE_BUTTON_IDS.translate
  // to ensure clicking the translate button triggers the unified translate tool
};

// ============================================================================
// Unified Content Collection
// ============================================================================

/**
 * Check if content is stale based on its timestamp.
 * Stale content (older than STALE threshold) should be skipped.
 *
 * @param timestamp - Content timestamp
 * @returns true if content is stale and should be skipped
 */
function isContentStale(timestamp: Date | undefined): boolean {
  if (!ENABLE_TIME_BASED_CONTEXT) return false;
  if (!timestamp) return false;

  const ageMs = Date.now() - new Date(timestamp).getTime();
  if (isNaN(ageMs)) return false;

  const timeRelevance = evaluateTimeRelevance(ageMs);
  return timeRelevance.category === 'stale';
}

/**
 * Collect all available content for video script generation.
 *
 * CRITICAL PRINCIPLE: Do NOT assume cached content is related to the current request.
 * Only use cached content if:
 * 1. User provided a direct query/document (Priority 1 - always use)
 * 2. This is an explicit chain step (isChainStep = true)
 * 3. User explicitly referenced prior content (userReferencedHistory = true)
 *
 * If none of these are true, return null (assume not related).
 *
 * Priority order (when allowed to use cached content):
 * 1. Direct query + document analysis (from attachments or orchestrator cache) - ALWAYS
 * 2. lastTranslatableContent (most recent workflow output) - IF explicitly referenced
 * 3. drSession.stage1Findings (in-progress research) - IF explicitly referenced
 * 4. lastResearchReport (completed research) - IF explicitly referenced
 */
export function collectVideoScriptContent(
  sharedContext: {
    lastTranslatableContent?: TranslatableContent | null;
    lastTranslatableContentRef?: React.MutableRefObject<TranslatableContent | null>;
    lastResearchReport?: ResearchReport | null;
    lastResearchReportRef?: React.MutableRefObject<ResearchReport | null>;
    drSession?: DeepResearchSession;
    drSessionRef?: React.MutableRefObject<DeepResearchSession>;
    documentContent?: string | null;
    getContentBySource?: (source: string) => TranslatableContent | null;
  },
  options: {
    query?: string;
    documentContent?: string | null;
    /** Skip time-based filtering (use when chaining workflows) */
    skipTimeFiltering?: boolean;
    /** TRUE = this is an explicit workflow chain step - use cached content */
    isChainStep?: boolean;
    /** TRUE = user explicitly referenced prior content ("that", "it", etc.) */
    userReferencedHistory?: boolean;
  }
): VideoScriptSourceContent | null {
  const shouldFilterByTime = ENABLE_TIME_BASED_CONTEXT && !options.skipTimeFiltering;

  // Priority 1: Direct query with optional document analysis
  // Direct queries are ALWAYS fresh - no assumptions needed
  if (options.query || options.documentContent) {
    const combined = [
      options.query || '',
      options.documentContent ? `\n\n--- Document Analysis ---\n${options.documentContent}` : ''
    ].filter(Boolean).join('');

    if (combined.trim()) {
      return {
        content: combined,
        title: options.query?.slice(0, 100) || 'Custom Video Script',
        source: 'research',
        timestamp: new Date(),
      };
    }
  }

  // CRITICAL: Only use cached content if explicitly referenced or chain step
  // Do NOT assume previous content is related to current request
  if (!options.isChainStep && !options.userReferencedHistory) {
    console.log('[VideoScript] No explicit reference to prior content - not assuming relatedness');
    return null;
  }

  console.log('[VideoScript] Using cached content:', {
    isChainStep: options.isChainStep,
    userReferencedHistory: options.userReferencedHistory,
  });

  // Priority 2: lastTranslatableContent (most recent workflow output)
  const translatableContent = sharedContext.lastTranslatableContentRef?.current ||
                              sharedContext.lastTranslatableContent;
  if (translatableContent?.content?.trim()) {
    // Check if content is stale
    if (shouldFilterByTime && isContentStale(translatableContent.timestamp)) {
      console.log('[VideoScript] lastTranslatableContent is stale (> 2 hours old), skipping');
    } else {
      return {
        content: translatableContent.content,
        title: translatableContent.title,
        source: translatableContent.source,
        timestamp: translatableContent.timestamp,
      };
    }
  }

  // Priority 3: In-progress deep research (Stage 1 findings)
  // Active sessions are considered fresh - no time filtering
  const drSession = sharedContext.drSessionRef?.current || sharedContext.drSession;
  if (drSession?.stage1Findings?.trim()) {
    const attachmentContent = drSession.attachmentContent || '';
    const combined = attachmentContent
      ? `${drSession.stage1Findings}\n\n--- Document Analysis ---\n${attachmentContent}`
      : drSession.stage1Findings;

    return {
      content: combined,
      title: drSession.originalQuery?.slice(0, 100) || 'Research Findings',
      source: 'research',
      timestamp: new Date(),
    };
  }

  // Priority 4: Completed research report
  const researchReport = sharedContext.lastResearchReportRef?.current ||
                         sharedContext.lastResearchReport;
  if (researchReport?.content?.trim()) {
    // Check if content is stale
    if (shouldFilterByTime && isContentStale(researchReport.timestamp)) {
      console.log('[VideoScript] lastResearchReport is stale (> 2 hours old), skipping');
    } else {
      return {
        content: researchReport.content,
        title: researchReport.title,
        source: 'research',
        timestamp: researchReport.timestamp,
      };
    }
  }

  // Priority 5: Content history fallback (especially useful in chained workflows)
  const historyResearch = sharedContext.getContentBySource?.('research');
  if (historyResearch?.content?.trim()) {
    console.log('[VideoScript] Using contentHistory research fallback');
    return {
      content: historyResearch.content,
      title: historyResearch.title,
      source: 'research',
      timestamp: historyResearch.timestamp,
    };
  }

  // Priority 6: workflow output store fallback
  const latestResearchOutput = getLatestOutputBySource('research');
  if (latestResearchOutput?.content?.trim()) {
    console.log('[VideoScript] Using workflowOutputStore research fallback');
    return {
      content: latestResearchOutput.content,
      title: latestResearchOutput.title,
      source: 'research',
      timestamp: latestResearchOutput.timestamp,
    };
  }

  const latestOutput = getLatestOutput();
  if (latestOutput?.content?.trim()) {
    console.log('[VideoScript] Using latest workflow output fallback:', latestOutput.source);
    return {
      content: latestOutput.content,
      title: latestOutput.title,
      source: latestOutput.source === 'translation' ? 'formatted' : latestOutput.source,
      timestamp: latestOutput.timestamp,
    };
  }

  // No content found
  return null;
}

// ============================================================================
// Trigger Pattern
// ============================================================================

/**
 * Match "write a video script [topic]" pattern.
 *
 * Phase 0 hardening: in-clause negation guard against the video-script
 * anchor keywords so "I don't want a video script, just text" doesn't
 * fire this tool.
 */
const VIDEO_SCRIPT_NEGATION_ANCHOR = /\b(video\s+script|guion(?:\s+de\s+video)?|gui[oó]n)\b/i;

function matchVideoScriptTrigger(text: string): { matches: boolean; query?: string } {
  const parsed = parseVideoScriptTrigger(text);
  if (parsed.isVideoScriptTrigger) {
    if (hasNegationAround(text, VIDEO_SCRIPT_NEGATION_ANCHOR)) {
      return { matches: false };
    }
    return { matches: true, query: parsed.query };
  }
  return { matches: false };
}

// ============================================================================
// Tool Definition
// ============================================================================

export const videoScriptTool: GlobalToolDefinition<VideoScriptState> = {
  id: 'video-script',
  name: 'Video Script',
  description: 'Generate professional video scripts from research content',

  triggerPattern: matchVideoScriptTrigger,

  createInitialState: (): VideoScriptState => ({
    stage: 'idle',
    sourceContent: null,
    stage1Analysis: null,
    userClarifications: [],
    scriptOutput: undefined,
    tokenUsages: undefined,
  }),

  steps: [
    // ========================================================================
    // Step 1: Initialize - Determine source content
    // ========================================================================
    {
      id: 'initialize',
      name: 'Initialize',
      activeDisplayName: 'preparing',

      execute: async (
        ctx: StepContext<VideoScriptState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        // Reset deep research RUNTIME when video script starts.
        // This clears the isAwaitingInput flag on the deep research runtime, preventing it
        // from intercepting user replies meant for video script.
        console.log('[VideoScript] Resetting deep research tool runtime to prevent message interception');
        ctx.sharedContext.resetToolRuntime('deep-research');
        ctx.sharedContext.resetDrSession();

        const input = rawInput as {
          query?: string;
          sourceContent?: VideoScriptSourceContent | null;
          awaitingTopic?: boolean;
          isChainStep?: boolean;
        } | undefined;

        // ========================================================================
        // Handle resume from topic prompt (user provided topic after being asked)
        // ========================================================================
        if (input?.awaitingTopic && ctx.userInput !== undefined) {
          if (isCancelCommand(ctx.userInput)) {
            ctx.messaging.showSpecialMessage({
              header: { title: 'video script cancelled', icon: '✕' },
              theme: 'green',
            });
            return { status: 'completed', nextStep: null };
          }

          // User provided topic - use it as content
          const topicContent: VideoScriptSourceContent = {
            content: ctx.userInput,
            title: ctx.userInput.slice(0, 100),
            source: 'research',
            timestamp: new Date(),
          };

          ctx.setState({ stage: 'stage1_analyzing', sourceContent: topicContent });
          return { status: 'completed', carryForward: { sourceContent: topicContent } };
        }

        // ========================================================================
        // Use pre-collected content OR collect now (unified path)
        // ========================================================================
        let sourceContent = input?.sourceContent || null;

        // PHASE 11: Chain step handling - use previous workflow output
        if (!sourceContent && input?.isChainStep) {
          console.log('[VideoScript] Chain step - checking for previous workflow output');
          const previousOutput = ctx.sharedContext.lastTranslatableContentRef?.current ||
                                 ctx.sharedContext.lastTranslatableContent;
          console.log('[VideoScript] lastTranslatableContent =', previousOutput ? `${previousOutput.source} (${previousOutput.content?.length || 0} chars)` : 'empty');

          if (previousOutput?.content?.trim()) {
            console.log('[VideoScript] Using previous workflow output as source content');
            sourceContent = {
              content: previousOutput.content,
              title: previousOutput.title || 'Video Script',
              source: previousOutput.source || 'research',
              timestamp: new Date(),
            };
          } else {
            const researchReport = ctx.sharedContext.lastResearchReportRef?.current ||
                                   ctx.sharedContext.lastResearchReport;
            console.log('[VideoScript] lastResearchReport =', researchReport ? `${researchReport.title || 'research'} (${researchReport.content?.length || 0} chars)` : 'empty');

            if (researchReport?.content?.trim()) {
              sourceContent = {
                content: researchReport.content,
                title: researchReport.title || 'Video Script',
                source: 'research',
                timestamp: researchReport.timestamp || new Date(),
              };
              console.log('[VideoScript] Using lastResearchReport fallback');
            } else {
              const historyResearch = ctx.sharedContext.getContentBySource?.('research');
              console.log('[VideoScript] contentHistory.research =', historyResearch ? `${historyResearch.title || 'research'} (${historyResearch.content?.length || 0} chars)` : 'empty');

              if (historyResearch?.content?.trim()) {
                sourceContent = {
                  content: historyResearch.content,
                  title: historyResearch.title || 'Video Script',
                  source: 'research',
                  timestamp: historyResearch.timestamp || new Date(),
                };
                console.log('[VideoScript] Using contentHistory research fallback');
              } else {
                const latestResearchOutput = getLatestOutputBySource('research');
                console.log('[VideoScript] workflowOutputStore.research =', latestResearchOutput ? `${latestResearchOutput.title || 'research'} (${latestResearchOutput.content?.length || 0} chars)` : 'empty');

                if (latestResearchOutput?.content?.trim()) {
                  sourceContent = {
                    content: latestResearchOutput.content,
                    title: latestResearchOutput.title || 'Video Script',
                    source: 'research',
                    timestamp: latestResearchOutput.timestamp || new Date(),
                  };
                  console.log('[VideoScript] Using workflowOutputStore research fallback');
                } else {
                  const latestOutput = getLatestOutput();
                  console.log('[VideoScript] workflowOutputStore.any =', latestOutput ? `${latestOutput.source} (${latestOutput.content?.length || 0} chars)` : 'empty');

                  if (latestOutput?.content?.trim()) {
                    sourceContent = {
                      content: latestOutput.content,
                      title: latestOutput.title || 'Video Script',
                      source: latestOutput.source === 'translation' ? 'formatted' : latestOutput.source,
                      timestamp: latestOutput.timestamp || new Date(),
                    };
                    console.log('[VideoScript] Using latest workflow output fallback');
                  }
                }
              }
            }
          }
        }

        if (!sourceContent) {
          // Handle attachments if present and not already cached
          // IMPORTANT: Check ref first (sync) then state (async) due to React state timing
          let documentContent = ctx.sharedContext.documentContentRef?.current ||
                                ctx.sharedContext.documentContent;
          const attachments = ctx.triggerMessage.attachments;
          const query = input?.query || '';

          if (!documentContent && hasAnalyzableFiles(attachments) && attachments) {
            const stream = ctx.messaging.showPending('reading attachment', 'video_script_attachment_pending');
            try {
              const result = await analyzeDocumentsWithClaude({
                files: attachments,
                userQuery: query || 'Analyze this document for video script creation',
                signal: ctx.abortSignal,
              });
              documentContent = result.analysis;
              ctx.sharedContext.setDocumentContent(documentContent);
              finalizeAttachmentReadComplete(stream, result, ctx.recordTransaction, {
                sourceTag: 'video_script_attachment_complete',
                transactionSummary: 'document analysis for video script',
              });
            } catch (err) {
              console.warn('attachment analysis failed, proceeding with text only', err);
              stream.finalize({
                payload: {
                  kind: 'video_script.error',
                  toolId: 'video-script',
                  severity: 'warning',
                  data: { errorMessage: 'document analysis failed', phase: 'attachment_read' },
                  summary: 'video script: attachment analysis failed; proceeding with query text only',
                },
                sourceTag: 'video_script_attachment_warning',
              });
            }
          }

          // Detect if user referenced prior content ("it", "that", "the research", etc.)
          const hasContinuitySignals = query
            ? detectTopicChangeSignals(query).hasContinuitySignals
            : false;

          // Use the UNIFIED content collection function
          // CRITICAL: Pass userReferencedHistory so cached content is only used when explicitly referenced
          sourceContent = collectVideoScriptContent(ctx.sharedContext, {
            query: query,
            documentContent,
            userReferencedHistory: hasContinuitySignals,
          });
        }

        // ========================================================================
        // No content: prompt for topic (instead of showing error)
        // ========================================================================
        if (!sourceContent || !sourceContent.content?.trim()) {
          ctx.messaging.showSpecialMessage({
            header: { title: 'what topic would you like a video script about?', icon: '?' },
            sections: [
              {
                type: 'text',
                content: 'I can create a video script for you. Please tell me what topic you\'d like covered.',
              },
              {
                type: 'text',
                content: 'You can also:',
              },
              {
                type: 'list',
                content: [
                  'Run deep research first, then click <strong>write video script</strong>',
                  'Type "write a video script about [topic]"',
                  'Attach a document with your content',
                ],
              },
            ],
            theme: 'green',
            helperText: 'Type your topic or attach a document to continue.',
          });

          return {
            status: 'awaiting-input',
            carryForward: { awaitingTopic: true },
          };
        }

        ctx.setState({
          stage: 'stage1_analyzing',
          sourceContent,
        });

        return {
          status: 'completed',
          carryForward: { sourceContent },
        };
      },
    },

    // ========================================================================
    // Step 2: Analyze - Run Stage 1 analysis with Claude
    // ========================================================================
    {
      id: 'analyze',
      name: 'Analyze Content',
      activeDisplayName: 'reading content',

      execute: async (
        ctx: StepContext<VideoScriptState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as {
          sourceContent: VideoScriptSourceContent;
          skipClarification?: boolean;  // Orchestrator can suggest skipping clarification
        } | undefined;
        const sourceContent = input?.sourceContent || ctx.state.sourceContent;
        const skipClarification = input?.skipClarification ?? false;

        // ========================================================================
        // Guard: If resuming from stage1_clarify, skip API call and proceed to generate
        // ========================================================================
        if (ctx.state.stage === 'stage1_clarify' && ctx.userInput !== undefined) {
          console.log('[VideoScript] Resuming from stage1_clarify - proceeding to generate step');

          // Check for cancel command
          if (isCancelCommand(ctx.userInput)) {
            ctx.messaging.showSpecialMessage({
              header: { title: 'video script generation cancelled', icon: '✕' },
              theme: 'green',
            });
            ctx.setState({ stage: 'idle' });
            return { status: 'completed', nextStep: null };
          }

          // Check for skip command (or empty input from button click)
          const isSkip = ctx.userInput.toLowerCase().trim() === 'skip' || ctx.userInput.trim() === '';

          // Build user clarifications from input
          const userClarifications: VideoScriptClarification[] = isSkip
            ? []
            : [{
                question: 'User-provided context',
                answer: ctx.userInput,
                timestamp: new Date(),
              }];

          // Proceed directly to generate step with existing stage1 analysis
          return {
            status: 'completed',
            carryForward: {
              sourceContent,
              stage1Result: ctx.state.stage1Analysis,
              userClarifications,
            },
          };
        }

        if (!sourceContent) {
          return {
            status: 'failed',
            error: new Error('No source content for analysis'),
          };
        }

        const stream = ctx.messaging.showPending('assessing', 'video_script_analysis_pending');

        try {
          const result = await runVideoScriptAnalysis({
            sourceContent: sourceContent.content,
            sourceTitle: sourceContent.title,
            sourceType: sourceContent.source,
            onChunk: () => {
              // No update needed - spinner shows until finalize()
            },
            onComplete: (_fullText, tokenUsage) => {
              // Store token usage for recording AFTER stream.finalize() (fixes timing issue)
              // Token usage is captured in result.tokenUsage after runVideoScriptAnalysis completes
            },
          });

          // Parse Stage 1 response
          const stage1Result = parseVideoScriptStage1Response(result.text);

          // Seed tokenUsages from upstream lineage (deep research → format report chain)
          const analyzeUpstreamUsages = ctx.sharedContext.lastTranslatableContentRef?.current?.tokenUsages || [];
          ctx.setState({
            stage: stage1Result.needsClarification ? 'stage1_clarify' : 'stage2_generating',
            stage1Analysis: stage1Result,
            tokenUsages: [
              ...analyzeUpstreamUsages,
              ...(result.tokenUsage ? [{ ...result.tokenUsage, summary: 'analysing for video script' }] : []),
            ],
          });

          // Determine whether to show clarifying questions:
          // - If orchestrator says skipClarification AND stage1 doesn't require it, skip
          // - If stage1 determines clarification IS needed, always show questions
          const shouldShowQuestions = stage1Result.needsClarification && !skipClarification;

          if (skipClarification) {
            console.log('[VideoScript] Orchestrator requested skipClarification - proceeding to generation');
          }

          if (shouldShowQuestions) {
            // Show clarifying questions and wait for user input
            const questionsHtml = formatVideoScriptClarifyingQuestionsHtml(
              stage1Result.summary.topic,
              stage1Result.clarifyingQuestions,
              BUTTON_IDS.proceed
            );

            stream.finalize({
              text: 'please answer the following questions to optimize your video script.',
              html: questionsHtml,
              sourceTag: 'video_script_questions',
              tokenUsage: result.tokenUsage ? { ...result.tokenUsage, summary: 'analysing for video script' } : undefined,
            });

            // Record transaction AFTER message display (fixes timing issue)
            if (result.tokenUsage) {
              ctx.recordTransaction({
                tokenUsage: { ...result.tokenUsage, summary: 'analysing for video script' },
                source: 'video_script_stage1',
                modelOverride: buildModelDisplay(result.tokenUsage, 'Script Analysis', PERPLEXITY_VIDEO_SCRIPT_STAGE1_DISPLAY),
                summary: 'analysing for video script',
              });
            }

            // Wait for user input
            return {
              status: 'awaiting-input',
              carryForward: { sourceContent, stage1Result },
            };
          } else {
            // No clarification needed - proceed directly to generation
            // Empty message to avoid redundant visual clutter (matches agenticWorkflows pattern)
            stream.finalize({
              text: '',
              html: '',
              sourceTag: 'video_script_analysis_complete',
              tokenUsage: result.tokenUsage ? { ...result.tokenUsage, summary: 'analysing for video script' } : undefined,
            });

            // Record transaction AFTER message display (fixes timing issue)
            if (result.tokenUsage) {
              ctx.recordTransaction({
                tokenUsage: { ...result.tokenUsage, summary: 'analysing for video script' },
                source: 'video_script_stage1',
                modelOverride: buildModelDisplay(result.tokenUsage, 'Script Analysis', PERPLEXITY_VIDEO_SCRIPT_STAGE1_DISPLAY),
                summary: 'analysing for video script',
              });
            }

            // Continue to generation, passing token usage for consolidation
            return {
              status: 'completed',
              carryForward: {
                sourceContent,
                stage1Result,
                userClarifications: [],
                analysisTokenUsage: result.tokenUsage,
              },
            };
          }
        } catch (error) {
          // Finalize with error + upstream lineage so info popup still shows workflow steps
          const errorUpstreamUsages = ctx.sharedContext.lastTranslatableContentRef?.current?.tokenUsages || [];
          stream.finalize({
            payload: {
              kind: 'video_script.error',
              toolId: 'video-script',
              severity: 'error',
              data: { errorMessage: formatVideoScriptError(error), phase: 'analysis' },
              summary: 'video script: analysis failed',
            },
            sourceTag: 'video_script_error',
            tokenUsages: errorUpstreamUsages.length > 0 ? errorUpstreamUsages : undefined,
          });
          return {
            status: 'failed',
            error: new Error(`Unable to analyze content: ${formatVideoScriptError(error)}`),
          };
        }
      },
    },

    // ========================================================================
    // Step 3: Generate - Run Stage 2 script generation with Claude
    // ========================================================================
    {
      id: 'generate',
      name: 'Generate Script',
      activeDisplayName: 'writing script',

      execute: async (
        ctx: StepContext<VideoScriptState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as {
          sourceContent: VideoScriptSourceContent;
          stage1Result: VideoScriptStage1Result;
          userClarifications?: VideoScriptClarification[];
          analysisTokenUsage?: TokenUsageMetadata;
        } | undefined;

        // Get clarifications from carryForward (already processed by analyze step)
        const userClarifications: VideoScriptClarification[] = input?.userClarifications || [];
        // Get analysis token usage from carryForward for consolidation
        const analysisTokenUsage = input?.analysisTokenUsage;

        const sourceContent = input?.sourceContent || ctx.state.sourceContent;
        const stage1Result = input?.stage1Result || ctx.state.stage1Analysis;

        if (!sourceContent || !stage1Result) {
          return {
            status: 'failed',
            error: new Error('Missing source content or analysis'),
          };
        }

        // Validate and provide defaults for stage1Summary fields
        const summary = stage1Result.summary;
        const validatedSummary: VideoScriptSummary = {
          topic: summary.topic || sourceContent.title || 'Immigration Law Topic',
          targetAudience: summary.targetAudience || 'General audience seeking immigration information',
          emotionalState: summary.emotionalState || 'Seeking clarity and guidance',
          platformAndLength: summary.platformAndLength || 'YouTube, 2-3 minutes',
          primaryGoalCta: summary.primaryGoalCta || 'Schedule a consultation to discuss your specific situation',
        };

        ctx.setState({
          stage: 'stage2_generating',
          userClarifications,
        });

        const stream = ctx.messaging.showPending('writing script', 'video_script_generation_pending');

        try {
          const result = await runVideoScriptGeneration({
            sourceContent: sourceContent.content,
            sourceTitle: sourceContent.title,
            stage1Summary: validatedSummary,
            userClarifications,
            onChunk: () => {
              // No update needed - spinner shows until finalize()
            },
            onComplete: (_fullText, tokenUsage) => {
              // Store token usage for recording AFTER stream.finalize() (fixes timing issue)
              // Token usage is captured in result.tokenUsage after runVideoScriptGeneration completes
            },
          });

          // Parse Stage 2 response
          const parsedScript = parseVideoScriptStage2Response(result.text);

          // MT-QA G6 (2026-05-08): log a warning when the generated word
          // count falls well outside the requested length budget. The
          // Stage 2 prompt now enforces ~165 WPM with an explicit per-length
          // word table, but if the model ignores it (e.g. returns a 520-word
          // script for a 60-second request) this gives us a visible signal
          // in production logs and attaches the mismatch to the info popup.
          try {
            const platformAndLength = (validatedSummary.platformAndLength || '').toLowerCase();
            const rangeMatch =
              platformAndLength.match(/(\d{1,3})\s*[-–to]+\s*(\d{1,3})\s*(second|sec|s|minute|min|m)\b/) ||
              null;
            const singleMatch =
              platformAndLength.match(/(\d{1,3})\s*(second|sec|s|minute|min|m)\b/) ||
              null;
            let minSeconds: number | null = null;
            let maxSeconds: number | null = null;
            const unitToSeconds = (n: number, unit: string): number =>
              /min|^m$/.test(unit) ? n * 60 : n;
            if (rangeMatch) {
              const unit = rangeMatch[3];
              minSeconds = unitToSeconds(parseInt(rangeMatch[1], 10), unit);
              maxSeconds = unitToSeconds(parseInt(rangeMatch[2], 10), unit);
            } else if (singleMatch) {
              const n = unitToSeconds(parseInt(singleMatch[1], 10), singleMatch[2]);
              minSeconds = Math.max(15, Math.round(n * 0.9));
              maxSeconds = Math.round(n * 1.15);
            }
            if (minSeconds != null && maxSeconds != null) {
              const WPM = 165;
              const minWords = Math.round((minSeconds * WPM) / 60);
              const maxWords = Math.round((maxSeconds * WPM) / 60);
              const wc = parsedScript.wordCount;
              if (wc < minWords * 0.6 || wc > maxWords * 1.4) {
                console.warn(
                  `[VideoScript] Word-count budget miss: requested ${minSeconds}-${maxSeconds}s (~${minWords}-${maxWords} words @ 165 wpm) but got ${wc} words. Platform+Length="${validatedSummary.platformAndLength}"`
                );
              }
            }
          } catch (wcErr) {
            // Non-fatal
            console.warn('[VideoScript] Word-count check threw:', wcErr);
          }

          // VALIDATION: Fail if the parsed script is empty (model returned malformed/empty output)
          if (!parsedScript.script || parsedScript.script.trim().length === 0) {
            console.error('[VideoScript] Parsed script is empty - model returned no usable content');
            console.error('[VideoScript] Raw response length:', result.text?.length || 0);
            // Finalize with error + full lineage so info popup shows all workflow steps
            const emptyScriptUpstreamUsages = ctx.sharedContext.lastTranslatableContentRef?.current?.tokenUsages || [];
            const emptyScriptUsages = [
              ...emptyScriptUpstreamUsages,
              ...(analysisTokenUsage ? [{ ...analysisTokenUsage, summary: 'analysing for video script' }] : []),
              ...(result.tokenUsage ? [{ ...result.tokenUsage, summary: 'write video script' }] : []),
            ];
            stream.finalize({
              payload: {
                kind: 'video_script.error',
                toolId: 'video-script',
                severity: 'error',
                data: {
                  errorMessage: 'video script generation returned empty content; the model may have timed out or produced an unparseable response',
                  phase: 'generation_empty',
                },
                summary: 'video script: generation returned empty content',
              },
              sourceTag: 'video_script_error',
              tokenUsages: emptyScriptUsages.length > 0 ? emptyScriptUsages : undefined,
            });
            ctx.setState({ stage: 'idle' });
            return {
              status: 'failed',
              error: new Error('Video script generation returned empty content. The model may have timed out or produced an unparseable response. Please try again.'),
            };
          }

          // Consolidate all token usages: upstream lineage + analysis + generation
          // MUST be built BEFORE setting translatableData (which references allTokenUsages)
          // Read upstream BEFORE overwriting lastTranslatableContentRef below
          const upstreamUsages = ctx.sharedContext.lastTranslatableContentRef?.current?.tokenUsages || [];
          const allTokenUsages: TokenUsageMetadata[] = [
            ...upstreamUsages,
            ...(analysisTokenUsage ? [{ ...analysisTokenUsage, summary: 'analysing for video script' }] : []),
            ...(result.tokenUsage ? [{ ...result.tokenUsage, summary: 'write video script' }] : []),
          ];

          // Update state
          ctx.setState({
            stage: 'complete',
            scriptOutput: {
              title: parsedScript.title,
              script: parsedScript.script,
              wordCount: parsedScript.wordCount,
              characterCount: parsedScript.characterCount,
              estimatedLength: parsedScript.estimatedLength,
            },
            tokenUsages: allTokenUsages,
          });

          // Set translatable content for Spanish translation workflow (with full lineage)
          const translatableData = {
            content: parsedScript.script,
            title: parsedScript.title,
            timestamp: new Date(),
            source: 'video_script' as const,
            tokenUsages: allTokenUsages.length > 0 ? allTokenUsages : undefined,
          };
          ctx.sharedContext.setLastTranslatableContent(translatableData);
          ctx.sharedContext.lastTranslatableContentRef.current = translatableData;

          // Format and finalize output
          // Use imported button ID from translate tool so clicking triggers the correct tool
          const outputHtml = formatVideoScriptOutputHtml(parsedScript, TRANSLATE_BUTTON_IDS.translate);

          stream.finalize({
            text: parsedScript.script,
            html: outputHtml,
            sourceTag: 'video_script_result',
            // Include all token usages in the info icon popup
            tokenUsages: allTokenUsages.length > 0 ? allTokenUsages : undefined,
            // Embed workflow output for downstream workflow chaining
            workflowOutput: {
              type: 'video_script',
              content: parsedScript.script,
              title: parsedScript.title,
              timestamp: new Date(),
              sourceWorkflow: 'video-script',
            },
          });

          // Record transaction AFTER message display (fixes timing issue)
          if (result.tokenUsage) {
            ctx.recordTransaction({
              tokenUsage: { ...result.tokenUsage, summary: 'write video script' },
              source: 'video_script_stage2',
              modelOverride: buildModelDisplay(result.tokenUsage, 'Script Generation', CLAUDE_VIDEO_SCRIPT_GENERATION_DISPLAY),
              summary: 'write video script',
            });
          }

          return {
            status: 'completed',
            output: {
              script: parsedScript.script,
              title: parsedScript.title,
            },
          };
        } catch (error) {
          // Finalize with error + upstream lineage so info popup still shows workflow steps
          const genErrorUpstreamUsages = ctx.sharedContext.lastTranslatableContentRef?.current?.tokenUsages || [];
          const genErrorUsages = [
            ...genErrorUpstreamUsages,
            ...(analysisTokenUsage ? [{ ...analysisTokenUsage, summary: 'analysing for video script' }] : []),
          ];
          stream.finalize({
            payload: {
              kind: 'video_script.error',
              toolId: 'video-script',
              severity: 'error',
              data: { errorMessage: formatVideoScriptError(error), phase: 'generation' },
              summary: 'video script: generation failed',
            },
            sourceTag: 'video_script_error',
            tokenUsages: genErrorUsages.length > 0 ? genErrorUsages : undefined,
          });
          ctx.setState({ stage: 'idle' });

          return {
            status: 'failed',
            error: new Error(`Unable to generate video script: ${formatVideoScriptError(error)}`),
          };
        }
      },

      cleanup: async (ctx: StepContext<VideoScriptState>) => {
        ctx.setState({ stage: 'idle' });
      },
    },
  ],

  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: VIDEO_SCRIPT_THEME,

  buttonIds: BUTTON_IDS,

  canTriggerFromButton: true,
};

// ============================================================================
// Export
// ============================================================================

export default videoScriptTool;
